import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  ArrowLeft, 
  User, 
  Bell, 
  Shield, 
  Building2, 
  Cloud, 
  Users, 
  CheckCircle,
  Trash2,
  Plus,
  Key,
  Mail,
  Briefcase,
  BookOpen,
  Settings as SettingsIcon,
  ClipboardCheck,
  Scale,
  Target,
  Zap
} from 'lucide-react';
import type { User as UserType } from '../App';
import { categories } from '../lib/mockData';
import { innovationReviewCriteria, ipProtectionTypes } from '../lib/reviewCriteria';

interface AccountSettingsProps {
  user: UserType;
  onBack: () => void;
}

export function AccountSettings({ user, onBack }: AccountSettingsProps) {
  const [saved, setSaved] = useState(false);
  
  // Common settings
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyReviewComplete, setNotifyReviewComplete] = useState(true);
  const [notifyNewSubmission, setNotifyNewSubmission] = useState(true);
  
  // Institution-specific
  const [institutionName, setInstitutionName] = useState(user.institution || 'University of Technology');
  const [institutionType, setInstitutionType] = useState('university');
  const [department, setDepartment] = useState('Innovation & Research');
  const [driveConnected, setDriveConnected] = useState(false);
  const [onedriveConnected, setOnedriveConnected] = useState(false);
  const [teraboxConnected, setTeraboxConnected] = useState(false);
  
  // Reviewer-specific
  const [expertiseAreas, setExpertiseAreas] = useState<string[]>(['Tech', 'Health']);
  const [bio, setBio] = useState('Expert reviewer with 15+ years of experience in innovation evaluation and Intellectual Property assessment.');
  const [autoAssign, setAutoAssign] = useState(true);
  const [maxReviewsPerWeek, setMaxReviewsPerWeek] = useState('10');

  // Admin-specific
  const [systemNotifications, setSystemNotifications] = useState(true);
  const [autoBackup, setAutoBackup] = useState(true);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const toggleExpertise = (category: string) => {
    if (expertiseAreas.includes(category)) {
      setExpertiseAreas(expertiseAreas.filter(c => c !== category));
    } else {
      setExpertiseAreas([...expertiseAreas, category]);
    }
  };

  const renderInnovatorSettings = () => (
    <div className="space-y-6">
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Update your profile details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization">Organization (Optional)</Label>
                <Input
                  id="organization"
                  placeholder="Your company or institution"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  rows={4}
                  placeholder="Tell us about yourself and your innovation focus..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Manage how you receive updates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-600" />
                    <Label>Email Notifications</Label>
                  </div>
                  <p className="text-slate-500">Receive updates via email</p>
                </div>
                <Switch
                  checked={notifyEmail}
                  onCheckedChange={setNotifyEmail}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Review Completed</Label>
                  <p className="text-slate-500">When your submission is reviewed</p>
                </div>
                <Switch
                  checked={notifyReviewComplete}
                  onCheckedChange={setNotifyReviewComplete}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Submission Confirmations</Label>
                  <p className="text-slate-500">Receive confirmation when you submit</p>
                </div>
                <Switch checked={true} onCheckedChange={() => {}} />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Platform Updates</Label>
                  <p className="text-slate-500">News and feature announcements</p>
                </div>
                <Switch checked={false} onCheckedChange={() => {}} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>Manage your account security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <Input id="current-password" type="password" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input id="new-password" type="password" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input id="confirm-password" type="password" />
              </div>

              <Button variant="outline" className="w-full">
                <Key className="w-4 h-4 mr-2" />
                Update Password
              </Button>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Two-Factor Authentication</Label>
                  <p className="text-slate-500">Add an extra layer of security</p>
                </div>
                <Button variant="outline" size="sm">Enable</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );

  const renderInstitutionSettings = () => (
    <div className="space-y-6">
      <Tabs defaultValue="institution" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="institution">Institution</TabsTrigger>
          <TabsTrigger value="storage">Cloud Storage</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="institution" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Institution Profile</CardTitle>
              <CardDescription>Manage your institution information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="inst-name">Institution Name</Label>
                <Input
                  id="inst-name"
                  value={institutionName}
                  onChange={(e) => setInstitutionName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="inst-type">Institution Type</Label>
                <Select value={institutionType} onValueChange={setInstitutionType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="university">University</SelectItem>
                    <SelectItem value="research">Research Institute</SelectItem>
                    <SelectItem value="college">College</SelectItem>
                    <SelectItem value="corporate">Corporate R&D</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-name">Administrator Name</Label>
                <Input
                  id="admin-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-email">Administrator Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="inst-description">Institution Description</Label>
                <Textarea
                  id="inst-description"
                  rows={4}
                  placeholder="Describe your institution's focus and innovation areas..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="storage" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>External Storage Connections</CardTitle>
              <CardDescription>
                Connect cloud storage for bulk innovation uploads
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <Cloud className="h-4 w-4" />
                <AlertDescription>
                  Connect your cloud storage to enable bulk upload of innovation portfolios from multiple researchers or students.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <Card className={driveConnected ? 'border-emerald-500' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Cloud className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-slate-900">Google Drive</div>
                          <div className="text-slate-500">
                            {driveConnected ? 'Connected' : 'Not connected'}
                          </div>
                        </div>
                      </div>
                      {driveConnected ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDriveConnected(false)}
                          >
                            Disconnect
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setDriveConnected(true)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className={onedriveConnected ? 'border-emerald-500' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Cloud className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-slate-900">Microsoft OneDrive</div>
                          <div className="text-slate-500">
                            {onedriveConnected ? 'Connected' : 'Not connected'}
                          </div>
                        </div>
                      </div>
                      {onedriveConnected ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setOnedriveConnected(false)}
                          >
                            Disconnect
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setOnedriveConnected(true)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className={teraboxConnected ? 'border-emerald-500' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Cloud className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-slate-900">TeraBox</div>
                          <div className="text-slate-500">
                            {teraboxConnected ? 'Connected' : 'Not connected'}
                          </div>
                        </div>
                      </div>
                      {teraboxConnected ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setTeraboxConnected(false)}
                          >
                            Disconnect
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setTeraboxConnected(true)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Affiliated Users</CardTitle>
                  <CardDescription>
                    Manage researchers and students who can submit under your institution
                  </CardDescription>
                </div>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Submissions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="text-slate-900">Dr. Sarah Mitchell</TableCell>
                    <TableCell>s.mitchell@university.edu</TableCell>
                    <TableCell>
                      <Badge variant="outline">Researcher</Badge>
                    </TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-600 hover:bg-emerald-700">Active</Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-slate-900">Prof. Michael Roberts</TableCell>
                    <TableCell>m.roberts@university.edu</TableCell>
                    <TableCell>
                      <Badge variant="outline">Faculty</Badge>
                    </TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-600 hover:bg-emerald-700">Active</Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-slate-900">Dr. Ahmed Al-Mansouri</TableCell>
                    <TableCell>a.almansouri@university.edu</TableCell>
                    <TableCell>
                      <Badge variant="outline">Researcher</Badge>
                    </TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-600 hover:bg-emerald-700">Active</Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Control institution-wide notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>New Submissions</Label>
                  <p className="text-slate-500">When researchers submit innovations</p>
                </div>
                <Switch
                  checked={notifyNewSubmission}
                  onCheckedChange={setNotifyNewSubmission}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Review Completed</Label>
                  <p className="text-slate-500">When submissions are reviewed</p>
                </div>
                <Switch
                  checked={notifyReviewComplete}
                  onCheckedChange={setNotifyReviewComplete}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Weekly Summary</Label>
                  <p className="text-slate-500">Portfolio performance digest</p>
                </div>
                <Switch checked={true} onCheckedChange={() => {}} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );

  const renderReviewerSettings = () => (
    <div className="space-y-6">
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="expertise">Expertise</TabsTrigger>
          <TabsTrigger value="criteria">Review Criteria</TabsTrigger>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Reviewer Profile</CardTitle>
              <CardDescription>Manage your professional information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reviewer-name">Full Name</Label>
                <Input
                  id="reviewer-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reviewer-email">Email Address</Label>
                <Input
                  id="reviewer-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reviewer-title">Professional Title</Label>
                <Input
                  id="reviewer-title"
                  placeholder="e.g., Senior Innovation Consultant, PhD"
                  defaultValue="Senior Innovation Consultant"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reviewer-organization">Organization/Affiliation</Label>
                <Input
                  id="reviewer-organization"
                  placeholder="Your organization or consultancy"
                  defaultValue="Innovation Advisory Group"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reviewer-bio">Professional Bio</Label>
                <Textarea
                  id="reviewer-bio"
                  rows={5}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="years-experience">Years of Experience</Label>
                <Select defaultValue="15">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5-10 years</SelectItem>
                    <SelectItem value="10">10-15 years</SelectItem>
                    <SelectItem value="15">15+ years</SelectItem>
                    <SelectItem value="20">20+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expertise" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Areas of Expertise</CardTitle>
              <CardDescription>
                Select your areas of expertise for better innovation matching
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <Briefcase className="h-4 w-4" />
                <AlertDescription>
                  Your expertise areas help our AI match you with relevant innovations for review.
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <Card
                    key={category}
                    className={`cursor-pointer transition-all ${
                      expertiseAreas.includes(category)
                        ? 'border-blue-600 bg-blue-50'
                        : 'hover:border-slate-400'
                    }`}
                    onClick={() => toggleExpertise(category)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-900">{category}</span>
                        {expertiseAreas.includes(category) && (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="specialized-skills">Specialized Skills</Label>
                <Textarea
                  id="specialized-skills"
                  rows={3}
                  placeholder="List specific skills, technologies, or methodologies (e.g., AI/ML, Clinical Trials, Patent Law, Intellectual Property Assessment...)"
                  defaultValue="AI/ML algorithms, Medical device evaluation, Clinical trial design, FDA approval process, Technology transfer"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry-experience">Industry Experience</Label>
                <Textarea
                  id="industry-experience"
                  rows={3}
                  placeholder="Describe your industry background..."
                  defaultValue="Healthcare technology, Medical devices, Pharmaceutical innovation, Biotech startups"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="criteria" className="space-y-6 mt-6">
          <Alert className="bg-blue-50 border-blue-200">
            <ClipboardCheck className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              <strong>Confidential:</strong> These review criteria are visible only to Independent Reviewers and are used to assess all innovation submissions systematically.
            </AlertDescription>
          </Alert>

          {innovationReviewCriteria.map((criteria, index) => (
            <Card key={criteria.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    {criteria.title}
                  </CardTitle>
                  <Badge variant="outline" className="bg-blue-50">
                    Weight: {criteria.weightage}%
                  </Badge>
                </div>
                <CardDescription>{criteria.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label>Assessment Points:</Label>
                  <ul className="space-y-2 ml-4">
                    {criteria.assessmentPoints.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-700">
                        <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ))}

          <Card className="bg-gradient-to-br from-slate-50 to-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                IP Protection Types Reference
              </CardTitle>
              <CardDescription>
                Guide for recommending appropriate IP protection strategies
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ipProtectionTypes.map((ip) => (
                  <Card key={ip.type}>
                    <CardContent className="pt-4 space-y-2">
                      <div className="text-slate-900">{ip.type}</div>
                      <p className="text-slate-600">{ip.description}</p>
                      <Separator />
                      <div>
                        <span className="text-slate-500">Suitable for: </span>
                        <span className="text-slate-700">{ip.suitable}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Duration: </span>
                        <span className="text-slate-700">{ip.duration}</span>
                      </div>
                      <div className="text-slate-600 text-xs">{ip.considerations}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preferences" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Review Preferences</CardTitle>
              <CardDescription>Configure your review workflow settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Auto-Assignment</Label>
                  <p className="text-slate-500">
                    Automatically assign matching innovations to you
                  </p>
                </div>
                <Switch checked={autoAssign} onCheckedChange={setAutoAssign} />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="max-reviews">Maximum Reviews Per Week</Label>
                <Select value={maxReviewsPerWeek} onValueChange={setMaxReviewsPerWeek}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 reviews/week</SelectItem>
                    <SelectItem value="10">10 reviews/week</SelectItem>
                    <SelectItem value="15">15 reviews/week</SelectItem>
                    <SelectItem value="20">20 reviews/week</SelectItem>
                    <SelectItem value="unlimited">Unlimited</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="preferred-stage">Preferred Innovation Stage</Label>
                <Select defaultValue="all">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    <SelectItem value="prototype">Prototype</SelectItem>
                    <SelectItem value="pilot">Pilot</SelectItem>
                    <SelectItem value="market-ready">Market Ready</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Priority Notifications</Label>
                  <p className="text-slate-500">
                    Get notified of high-priority innovations immediately
                  </p>
                </div>
                <Switch checked={true} onCheckedChange={() => {}} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Notification Settings</CardTitle>
              <CardDescription>Manage how you receive review updates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Email Notifications</Label>
                  <p className="text-slate-500">Receive updates via email</p>
                </div>
                <Switch checked={notifyEmail} onCheckedChange={setNotifyEmail} />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>New Assignment Alerts</Label>
                  <p className="text-slate-500">When innovations are assigned to you</p>
                </div>
                <Switch
                  checked={notifyNewSubmission}
                  onCheckedChange={setNotifyNewSubmission}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Review Reminders</Label>
                  <p className="text-slate-500">Remind me of pending reviews</p>
                </div>
                <Switch checked={true} onCheckedChange={() => {}} />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Daily Digest</Label>
                  <p className="text-slate-500">Summary of pending and completed reviews</p>
                </div>
                <Switch checked={true} onCheckedChange={() => {}} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );

  const renderAdminSettings = () => (
    <div className="space-y-6">
      <Tabs defaultValue="system" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="system">System</TabsTrigger>
          <TabsTrigger value="institutions">Institutions</TabsTrigger>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
          <TabsTrigger value="profile">Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="system" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>System Configuration</CardTitle>
              <CardDescription>Manage platform-wide settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>System Notifications</Label>
                  <p className="text-slate-500">Critical system alerts and updates</p>
                </div>
                <Switch
                  checked={systemNotifications}
                  onCheckedChange={setSystemNotifications}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Automatic Backups</Label>
                  <p className="text-slate-500">Daily automated database backups</p>
                </div>
                <Switch checked={autoBackup} onCheckedChange={setAutoBackup} />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Default Review Deadline</Label>
                <Select defaultValue="14">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="21">21 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>AI Matching Sensitivity</Label>
                <Select defaultValue="medium">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low (70%+ match)</SelectItem>
                    <SelectItem value="medium">Medium (80%+ match)</SelectItem>
                    <SelectItem value="high">High (90%+ match)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Data Management</CardTitle>
              <CardDescription>Export and backup options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" className="w-full">
                <BookOpen className="w-4 h-4 mr-2" />
                Export All Data
              </Button>
              <Button variant="outline" className="w-full">
                <Shield className="w-4 h-4 mr-2" />
                Generate Backup
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="institutions" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Registered Institutions</CardTitle>
                  <CardDescription>Manage institutional accounts</CardDescription>
                </div>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Institution
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Institution</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Submissions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <span className="text-slate-900">University of Technology</span>
                      </div>
                    </TableCell>
                    <TableCell>University</TableCell>
                    <TableCell>156</TableCell>
                    <TableCell>3</TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-600 hover:bg-emerald-700">Active</Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm">Manage</Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <span className="text-slate-900">State University</span>
                      </div>
                    </TableCell>
                    <TableCell>University</TableCell>
                    <TableCell>203</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-600 hover:bg-emerald-700">Active</Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm">Manage</Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <span className="text-slate-900">Innovation Hub College</span>
                      </div>
                    </TableCell>
                    <TableCell>College</TableCell>
                    <TableCell>87</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-600 hover:bg-emerald-700">Active</Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm">Manage</Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="permissions" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>User Permissions</CardTitle>
              <CardDescription>Configure role-based access control</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-slate-900 mb-4">Reviewer Permissions</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Can review innovations</Label>
                    <Switch checked={true} onCheckedChange={() => {}} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Can view all submissions</Label>
                    <Switch checked={true} onCheckedChange={() => {}} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Can export reports</Label>
                    <Switch checked={false} onCheckedChange={() => {}} />
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-slate-900 mb-4">Institution Permissions</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Can bulk upload</Label>
                    <Switch checked={true} onCheckedChange={() => {}} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Can manage affiliated users</Label>
                    <Switch checked={true} onCheckedChange={() => {}} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Can view analytics</Label>
                    <Switch checked={true} onCheckedChange={() => {}} />
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-slate-900 mb-4">Innovator Permissions</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Can submit innovations</Label>
                    <Switch checked={true} onCheckedChange={() => {}} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Can edit pending submissions</Label>
                    <Switch checked={true} onCheckedChange={() => {}} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Can view review history</Label>
                    <Switch checked={true} onCheckedChange={() => {}} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profile" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Administrator Profile</CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-name">Full Name</Label>
                <Input id="admin-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-email">Email Address</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-role">Role</Label>
                <Input id="admin-role" value="System Administrator" disabled />
              </div>

              <Separator />

              <Button variant="outline" className="w-full">
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-slate-900">Account Settings</h1>
            <p className="text-slate-600">
              Manage your {user.role} account preferences and configuration
            </p>
          </div>
        </div>
        <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
          <CheckCircle className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>

      {saved && (
        <Alert className="mb-6 bg-emerald-50 border-emerald-200">
          <CheckCircle className="h-4 w-4 text-emerald-600" />
          <AlertDescription className="text-emerald-900">
            Your settings have been saved successfully.
          </AlertDescription>
        </Alert>
      )}

      {user.role === 'innovator' && renderInnovatorSettings()}
      {user.role === 'institution' && renderInstitutionSettings()}
      {user.role === 'reviewer' && renderReviewerSettings()}
      {user.role === 'admin' && renderAdminSettings()}
    </div>
  );
}